import { BenchmarkService } from './benchmarkService';

interface CachedBenchmarkDates {
  dates: string[];
  model: string | undefined;
  timestamp: number;
}

export class BenchmarkDatesService {
  private static instance: BenchmarkDatesService;
  private cache: Map<string, CachedBenchmarkDates> = new Map();
  private readonly CACHE_DURATION = 2 * 60 * 60 * 1000; // 2 hours in milliseconds
  private benchmarkService: BenchmarkService | null = null;
  private getToken: (() => Promise<string>) | null = null;

  private constructor() {}

  static getInstance(): BenchmarkDatesService {
    if (!BenchmarkDatesService.instance) {
      BenchmarkDatesService.instance = new BenchmarkDatesService();
    }
    return BenchmarkDatesService.instance;
  }

  /**
   * Initialize the service with auth token getter
   */
  initialize(getToken: () => Promise<string>, baseUrl: string) {
    this.getToken = getToken;
    this.benchmarkService = new BenchmarkService(baseUrl, getToken);
  }

  /**
   * Get cache key for a model
   */
  private getCacheKey(model?: string): string {
    return model || 'all_models';
  }

  /**
   * Check if cached data is still valid
   */
  private isCacheValid(cachedData: CachedBenchmarkDates): boolean {
    return Date.now() - cachedData.timestamp < this.CACHE_DURATION;
  }

  /**
   * Preload available dates for commonly used models
   */
  async preloadAvailableDates(): Promise<void> {
    if (!this.benchmarkService) {
      console.warn('BenchmarkDatesService not initialized');
      return;
    }

    // Preload for most common models
    const modelsToPreload = ['ept2', 'ept2_early', 'ept1_5', 'aifs'];

    try {
      // Load dates for each model in parallel
      await Promise.all([
        this.getAvailableDates(), // All models
        ...modelsToPreload.map((model) => this.getAvailableDates(model)),
      ]);

      console.log('✅ Benchmark dates preloaded successfully for models:', [
        'all',
        ...modelsToPreload,
      ]);
    } catch (error) {
      console.warn('Failed to preload benchmark dates:', error);
    }
  }

  /**
   * Get available dates with caching
   */
  async getAvailableDates(model?: string): Promise<string[]> {
    if (!this.benchmarkService) {
      console.warn('BenchmarkDatesService not initialized');
      return [];
    }

    const cacheKey = this.getCacheKey(model);
    const cached = this.cache.get(cacheKey);

    // Return cached data if valid
    if (cached && this.isCacheValid(cached)) {
      return cached.dates;
    }

    try {
      // Fetch from API
      const response = await this.benchmarkService.getAvailableDates(model, 365);

      if (response.data) {
        const dates = response.data.dates;

        // Cache the result
        this.cache.set(cacheKey, {
          dates,
          model,
          timestamp: Date.now(),
        });

        return dates;
      }
    } catch (error) {
      console.warn(`Failed to fetch available dates for model ${model}:`, error);
    }

    // Return cached data even if expired, or empty array
    return cached ? cached.dates : [];
  }

  /**
   * Clear cache for a specific model or all models
   */
  clearCache(model?: string): void {
    if (model) {
      this.cache.delete(this.getCacheKey(model));
    } else {
      this.cache.clear();
    }
  }

  /**
   * Get cache status for debugging
   */
  getCacheStatus(): Array<{ model: string; dates: number; age: number }> {
    const now = Date.now();
    return Array.from(this.cache.entries()).map(([key, data]) => ({
      model: key,
      dates: data.dates.length,
      age: Math.round((now - data.timestamp) / 1000), // age in seconds
    }));
  }
}

// Export singleton instance
export const benchmarkDatesService = BenchmarkDatesService.getInstance();
